import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import {
  Allergen,
  ContainmentLevel,
  GTINMetadata,
} from '@/contexts/item-context-types';
import { AllergenInfo } from './allergen-info';
import { ItemDetail_GtinMetadataFieldsFragment } from '@/types';
import { NutritionLabel } from '@/components/nutrition-label';
import { RichInfoProductSpecs } from './rich-info-product-specs';
import { RichInfoSection } from './rich-info-section';
import { StyleSheet, Text } from 'react-native';
import { TYPOGRAPHY } from '@/styles';

interface Props {
  gtinMetadata: ItemDetail_GtinMetadataFieldsFragment;
  horizontal?: boolean;
}

export const GTINRichInfo: React.FC<Props> = ({ gtinMetadata, horizontal }) => {
  const { metadata } = gtinMetadata;
  const typedMetadata = metadata as GTINMetadata;

  const [freeFromAllergens, containsAllergens] = useMemo<
    [Allergen[], Allergen[]]
  >(() => {
    const freeFrom: Allergen[] = [];
    const contains: Allergen[] = [];
    const allAllergens = typedMetadata.allergens || [];
    for (const allergen of allAllergens) {
      if (allergen.containment_level === ContainmentLevel.Contains) {
        contains.push(allergen);
      } else {
        freeFrom.push(allergen);
      }
    }

    return [freeFrom, contains];
  }, [typedMetadata]);

  return (
    <>
      {freeFromAllergens.length > 0 ? (
        <RichInfoSection horizontal={horizontal} title={i18n.t('free_from')}>
          <AllergenInfo allergens={freeFromAllergens} />
        </RichInfoSection>
      ) : null}
      {containsAllergens.length > 0 ? (
        <RichInfoSection horizontal={horizontal} title={i18n.t('contains')}>
          <AllergenInfo allergens={containsAllergens} />
        </RichInfoSection>
      ) : null}
      {typedMetadata.ingredients ? (
        <RichInfoSection horizontal={horizontal} title={i18n.t('ingredients')}>
          <Text style={[TYPOGRAPHY.small, styles.ingredients]}>
            {metadata.ingredients}
          </Text>
        </RichInfoSection>
      ) : null}
      {typedMetadata.nutritional_info ? (
        <RichInfoSection
          horizontal={horizontal}
          title={i18n.t('nutrition_facts')}
        >
          <NutritionLabel
            info={typedMetadata.nutritional_info}
            tabular={horizontal}
          />
        </RichInfoSection>
      ) : null}
      <RichInfoProductSpecs
        gtinMetadata={gtinMetadata}
        horizontal={horizontal}
      />
    </>
  );
};

const styles = StyleSheet.create({
  ingredients: {
    lineHeight: 16,
    // behaves differently on web and native
    // web will keep everything uppercase
    textTransform: 'capitalize',
  },
});
